// SwmmReactUI.js

// Components 
import MenuPrimary from '../MenuPrimary/MenuPrimary'
import MenuIconRibbonButtons from '../MenuIconRibbonButtons/MenuIconRibbonButtons'
import MenuInpTables from '../MenuInpTables/MenuInpTables'
//import { MultiFilesExample } from '../MultiFilesExample'
import DialogWrapper from "./DialogWrapper"
import { useState, useEffect } from 'react'
import { OpenDefaultInpFileHeadless } from './OpenDefaultInpFileHeadless'
import { OpenDefaultRptFileHeadless } from './OpenDefaultRptFileHeadless'
import { OpenDefaultOutFileHeadless } from './OpenDefaultOutFileHeadless'
import { SwmmRun } from './SwmmRun'
import { ModelToText } from './ModelToText'
import { Box } from '@mui/material'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { SwmmGeoJSON } from '@fileops/swmm-node'
import Draggable from "react-draggable"
import { renameNode, renameLink, renameSubcatchment, renameRaingage } from '../SwmmNode_Staging/EditModel'
import 'mapbox-gl/dist/mapbox-gl.css'
import DemoCodeClickChart from './DemoCodeClickChart'
import ColorsDialog from '../UI_Colors/ColorsDialog'
import ObjectKeysMenu from '../MenuInpTables/ObjectKeysMenu'
import GroundwaterDialog from '../UI_Groundwater/GroundwaterDialog'
import LIDKeysDialog from '../UI_LIDControl/LIDKeysDialog'
import DialogWindows from '../DialogWindows/DialogWindows'
import RptDisplayDialog from "./RptDisplayDialog"
import ModelDisplayDialog from "./ModelDisplayDialog"
import OptionsDialog from "../UI_Options/OptionsDialog"
import ClimatologyDialog from "../UI_Climatology/ClimatologyDialog"
import InflowsDialog from "../UI_Inflows/InflowsDialog"
import TreatmentDialog from "../UI_Treatment/TreatmentDialog"
import InfiltrationDialog from "../UI_Infiltration/InfiltrationDialog"
import LoadingsDialog from "../UI_Loadings/LoadingsDialog"
import LandUsesDialog from "../UI_LandUses/LandUsesDialog"
import GWFDialog from "../UI_GWF/GWFDialog"
import CoveragesDialog from "../UI_Coverages/CoveragesDialog"
import InitialBuildupDialog from "../UI_InitialBuildup/InitialBuildupDialog"
import SubcatchmentPatternsDialog from "../UI_SubcatchmentPatterns/SubcatchmentPatternsDialog"
import ShapeDialog from "../UI_Shape/ShapeDialog"
import MapboxGLControl from '../Mapbox_Components/MapboxGLControl'

import '../../css/mapbox.css'
import '../../css/article.css'
import '../../css/objectmodal.css'
import '../../css/testcss.css'
import "../../css/DemoCode.css"

const defaultDialogSettings = {x: 110, y: 110, height: 500, width: 320}

/**
 * Main swmmReact UI component. Includes most menus, mapping tools, and dialogs
 * 
 * @param {JSON} model state variable containing an EPA-SWMM model in JSON format
 * @param {function} setModel sets the swmm model in JSON format
 * @param {ArrayBuffer} swmmOut the contents of an EPA-SWMM .out file in array buffer format.
 * @param {function} setSwmmOut sets the swmm .out file contents
 * @param {string} height css height ('400px', '10%') of the interface. 400px is a good size - smaller can interfere with the vertical menus.
 * @param {string} inpFile the path and name of an epa-swmm .inp file, with file extension (.inp)
 * @param {string} rptFile the path and name of an epa-swmm .rpt file, with file extension (.rpt)
 * @param {boolean} demos if true, displays the optional 'Demos' button inside the 'File' menu ribbon option.
 * @param {object} view if not null, contains the bounds, pitch, and bearing of the model display.
 * @param {boolean} mapOnly if true, displays the map without any menus. Great for close-ups and non-editable models.
 * @param {string} mainTextColor color for most of the UI text.
 * @param {string} backgroundColor background color for most of the UI components.
 * @param {boolean} loadNew if true, displays the optional 'Load' button inside the 'File' mdenu ribbon option.
 * @param {number} modelRun flag, signals to the parent component that the model has been run and new output data is available.
 * @param {function} setModelRun sets the modelRun state flag.
 * @returns JSX component for displaying a swmmReact UI.
 */
export default function SwmmReactUI({model, setModel, swmmOut, setSwmmOut, height, inpFile, rptFile, outFile, demos=false, view=null, mapOnly=false, mainTextColor='white', backgroundColor='black', loadNew=true, modelRun, setModelRun, rpt, setRpt, showResults=false}){
  // Top z-index for draggable windows
  const [zIndex, setZIndex] = useState(3000)
  // Positions for draggable windows
  const [keysDialogPosition, setKeysDialogPosition] = useState({x: 100, y: 100, height: 500, width: 320})
  const [uiDialogPosition, setUIDialogPosition] = useState({x: 100, y: 100, height: 600, width: 320})

  // Reload file using this key in a file loader component like OpenDefaultInpFileHeadless
  const [reloadFile] = useState('reload')
  // Spinner for when model is running
  const [runSpinner, setRunSpinner] = useState(false)
  // Change the map style
  const [mapStyle, setMapStyle] = useState(3)
  const [showLabels, setShowLabels] = useState(false)
  const [loadModel, setLoadModel] = useState(0)
  // name of the input file
  const [swmmInp, setSwmmInp] = useState(model != null?'':'./extraResources/newFile.inp')//useState(inpFile!=''?inpFile:'./extraResources/newFile.inp')
  // text contents of the  input file
  const [swmmTxt, setSwmmTxt] = useState('')
  // contents of the .rpt file
  //const [rpt, setRpt] = useState()
  // Model/map editing
  const [editType, setEditType] = useState(0)
  // Used for the ObjectKeysMenu editing system.
  const [keysSection, setKeysSection] = useState(undefined)
  const [subkeysSection, setSubkeysSection] = useState(undefined)
  // Passed to ObjectKeysMenu to indicate what happens when the "+" button is clicked.
  const [createFunction, setCreateFunction] = useState(()=>{})
  // Passed to ObjectKeysMenu to indicate what happens when the "-" button is clicked.
  const [deleteFunction, setDeleteFunction] = useState(()=>{})

  const [geoJSONData, setGeoJSONData] = useState({})
  const [openMapObject, setOpenMapObject] = useState(false)

  const [targetID, setTargetID] = useState('')
  const [targetType, setTargetType] = useState('')
  const [buttonColor, setButtonColor] = useState("#2b31e3")
  const [textColor,   setTextColor]   = useState(mainTextColor)
  // For changing names using input form
  const [newName, setNewName] = useState('')
  // Testing this function for external code interfaces. Not necessary for normal usage.
  /*const runModel = () => {
    setModelRun(prev=>prev+1)
  }*/

  // Every time a zIndex is assigned, it must be incremented
  const handleZIndexAssign = () => {
    const val = zIndex + 1
    setZIndex(val)
    return val
  }

/*******************************************************
 * DIALOG MANAGEMENT
 * 
 */

  // Names of consistent dialogs
  const dialogNames = ['ModelText',       'RptText',        'Options', 'Climatology',
                      'KeysMenu',         'Shape',          'Inflows', 'Treatment',
                      'Infiltration',     'Groundwater',    'GWF',     'Loadings', 
                      'Coverages',        'InitialBuildup', 'SubcatchmentPatterns',
                      'LIDKeys', 'LandUses',
                      'Colors', 'Code'/*, 'MapObject'*/]

  // Use when all draggables and modals need to close.
  const handleClose = () => {
    setEditType(0)
    setOpenMapObject(false)
    dialogNames.forEach(v=>{
      // Don't close the KeysMenu, if it is open.
      if(v !== 'KeysMenu')
        dialogStates[v].setOpen(false)
    })

    // These rename/update/delete functions should be part of swmmNode, not part of the UI.
    if(newName !== targetID && newName !== '' && ['JUNCTIONS', 'STORAGE', 'OUTFALLS', 'DIVIDERS'].includes(targetType)){
      renameNode(model, setModel, targetType, targetID, newName)
    } else 
    if(newName !== targetID && newName !== '' && ['CONDUITS', 'PUMPS', 'ORIFICES', 'WEIRS', 'OUTLETS'].includes(targetType)){
      renameLink(model, setModel, targetType, targetID, newName)
    } else 
    if(newName !== targetID && newName !== '' && ['SUBCATCHMENTS'].includes(targetType)){
      renameSubcatchment(model, setModel, targetType, targetID, newName)
    } else 
    if(newName !== targetID && newName !== '' && ['RAINGAGES'].includes(targetType)){
      renameRaingage(model, setModel, targetType, targetID, newName)
    }
    setNewName('')
  }

  const dialogPositions = {}
  const dialogStates = {}
  
  for (const name of dialogNames) {
    // eslint-disable-next-line
    const [position, setPosition] = useState(name==='RptText'?{x: 110, y: 110, height: 100, width: 320}:defaultDialogSettings)
    dialogPositions[name] = { position, setPosition }

    // eslint-disable-next-line
    const [open, setOpen] = useState(false)
    dialogStates[name] = { open, setOpen }
  }

    // parameters for dialogBoxes
    const dialogParams = [
      { name: 'ModelText', minHeight: 50, component: ModelDisplayDialog, props: {model:swmmTxt}},
      { name: 'RptText', minHeight: 50, component: RptDisplayDialog,   props: {rpt:rpt}},
      { name: 'Options',   minHeight: 500, component: OptionsDialog,      props: {model:model, setModel: setModel}},
      { name: 'Climatology',   minHeight: 550, component: ClimatologyDialog,   props: {model:model, setModel: setModel}},
      { name: 'Inflows',   minHeight: 500, component: InflowsDialog,      props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      { name: 'Treatment',   minHeight: 500, component: TreatmentDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      
      { name: 'Infiltration',   minHeight: 500, component: InfiltrationDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      { name: 'Loadings',   minHeight: 500, component: LoadingsDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      { name: 'GWF',   minHeight: 500, component: GWFDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      { name: 'Coverages',   minHeight: 500, component: CoveragesDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      { name: 'InitialBuildup',   minHeight: 500, component: InitialBuildupDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      { name: 'SubcatchmentPatterns',   minHeight: 500, component: SubcatchmentPatternsDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      { name: 'Shape',   minHeight: 500, component: ShapeDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      { name: 'LandUses', minHeight: 500, component: LandUsesDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID}},
      { name: 'Colors',   minHeight: 500, component: ColorsDialog,  props: {buttonColor:buttonColor, setButtonColor:setButtonColor, textColor:textColor, setTextColor:setTextColor}},
      { name: 'Groundwater',   minHeight: 500, component: GroundwaterDialog,  props: {model:model, setModel: setModel, targetType:targetType, target:targetID, setOpenGWF:dialogStates['GWF'].setOpen}},
    ]

    const mapObjectDialogParams = {name: 'MapObject', minHeight: 500, component: DemoCodeClickChart, props: {model:model, setModel:setModel, swmmOut:swmmOut, targetType:targetType, target:targetID, setNewName:setNewName, dialogStates:dialogStates}}

/**********************************************************
 * EFFECTS SECTION
 * 
 */

  // Code window variable and function exposure
  /*useEffect(()=> {
    console.log(model)
    //window.model = model
    //window.swmmOut = swmmOut
    //window.ModelToText = ModelToText
    //window.setModel = setModel
    //window.runModel = runModel
    //setCodeState(prev=>prev+1)
  }, [model, swmmOut])*/

  // If an escape key is pressed during map editing, reset the edit layer.
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        setEditType(0)
      }
    }
    window.addEventListener('keydown', handleEsc)

    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, )

  // If there is a valid model, parse the model for geojson data.
  useEffect(()=>{
    if(model !== null && typeof model !== 'undefined' && ('PCS' in model)){
      let globalGeojson = SwmmGeoJSON.spatialProjection(model)
      //console.log(globalGeojson)
        
      setGeoJSONData(JSON.parse(JSON.stringify(globalGeojson)))
    }
  }, [model])

/*************************************************
 * UTILITY FUNCTIONS SECTION
 * 
 */

  /**
   * Prepare the UI for ObjectKeysMenu dialog.
   * 
   * @param {string} keysSection Main table of the .inp file to be modified.
   * @param {string} subKeysSection Subtable of an .inp file to be modified.
   * @param {number} createType Edit type used for 'create' actions.
   * @param {number} deleteType Edit type used for 'delete' actions.
   * @returns null
   */
  function setupObjectKeysMenu(keysSection, subKeysSection, createType, deleteType) {
    setKeysSection(keysSection)
    setSubkeysSection(subKeysSection)
    dialogStates['KeysMenu'].setOpen((!dialogStates['KeysMenu'].open))
    if(createType != null){
      setCreateFunction(()=>()=>{setEditType(createType)})
    } else {
      setCreateFunction(null)
    }
    if(deleteType != null){
      setDeleteFunction(()=>()=>{setEditType(deleteType)})
    } else {
      setDeleteFunction(null)
    }
  }

  /***************************************** 
   * STYLES
   * 
   *****************************************/
  // Dark themes for MUI Typography/Boxes
  const darkTheme = createTheme({
    palette: {
      color: textColor,
      mode: 'dark',
      marginLeft: '10px',
      paddingLeft: '10px',
      spacing: '10px'
    },
  })
  // Dialog box styles
  const boxStyle = {
    bgcolor: 'background.paper',
    border: '5px solid rgba(200, 200, 200, 0.1)',
    borderRadius: '15px', 
    boxShadow: 24,
    p: 4,
  }

  // Icon Menu ribbon
  const iconMenuRibbonStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    borderBottom: "1px solid #444",
    paddingLeft: '5px',  
    color:'white', 
  }

  /*******************************************************
   * CUSTOM COLORS
   * 
   * 
   */
  const { palette } = createTheme()
  const { augmentColor } = palette
  const createColor = (mainColor) => augmentColor({ color: { main: mainColor } })
  const interactiveTheme = createTheme({
    color: textColor,
    palette: {
      color: textColor,
      mode: 'dark',
      marginLeft: '10px',
      paddingLeft: '10px',
      spacing: '10px',
      buttonColor: createColor(buttonColor),
    },
    // Ensure tooltips do not remain when hovered.
    components: {
      // Name of the component
      MuiTooltip: {
        defaultProps: {
          disableInteractive: true
        }
      },
      // Name of the component
      MuiTypography: {
        styleOverrides: {
          // Name of the slot
          root: {
            color: textColor,
          },
        },
      },
      // Name of the component
      MuiButton: {
        styleOverrides: {
          // Name of the slot
          root: {
            color: textColor,
            textTransform: 'none'
          },
        },
      },
      // Name of the component
      MuiModal: {
        styleOverrides: {
          // Name of the slot
          root: {
            color: textColor,
          },
        },
      },
    },
  })

  
  // Use to change mapbox style
  const changeMapStyle = () => {
    setMapStyle(prev=>(prev+1)%4)
  }

  // If the user has supplied an input file, open that file on first render
  useEffect(()=> {
    if(inpFile != null){ setSwmmInp(inpFile) }
  }, [inpFile])

  // If the user has supplied a report file, open that file on first render
  /*useEffect(()=> {
    if(rptFile != null){ setRpt(rptFile) }
  }, [rptFile])*/

  // If the user has supplied an output file, open that file on first render
  /*useEffect(()=> {
    if(outFile != null){ setRpt(outFile) }
  }, [rptFile])*/

  return (
    <>
    <div style={{display: 'flex', flexDirection: 'column', height: height, backgroundColor: backgroundColor}}>
      <ThemeProvider theme={interactiveTheme} >
        {/* Open a swmm model, translate to JSON */}
        {swmmInp !== '' && <OpenDefaultInpFileHeadless key={reloadFile} setModel={setModel} setSwmmOut={setSwmmOut} fileName={swmmInp} setSwmmTxt={setSwmmTxt} setRpt={setRpt} setLoadModel={setLoadModel} closeAll={handleClose} reloadFile={reloadFile} />}
        {<OpenDefaultRptFileHeadless setModel={setModel} setSwmmOut={setSwmmOut} fileName={rptFile} setSwmmTxt={setSwmmTxt} setRpt={setRpt} setLoadModel={setLoadModel} closeAll={handleClose} />}
        {<OpenDefaultOutFileHeadless setModel={setModel} setSwmmOut={setSwmmOut} fileName={outFile} setSwmmTxt={setSwmmTxt} setRpt={setRpt} setLoadModel={setLoadModel} closeAll={handleClose} />}
        {
          !mapOnly &&
          <>
            <div className="navi">
              <MenuPrimary reloadFile={reloadFile} setModel={setModel} setSwmmInp={setSwmmInp} 
                          setSwmmOut={setSwmmOut} rpt={rpt} setRpt={setRpt} swmmTxt={swmmTxt} setEditType={setEditType}
                          setModelRun={setModelRun} setSwmmTxt={setSwmmTxt} setLoadModel={setLoadModel} handleClose={handleClose} demos={demos} changeMapStyle={changeMapStyle} dialogStates={dialogStates} backgroundColor={backgroundColor} mainTextColor={mainTextColor} loadNew={loadNew} showLabels={showLabels} setShowLabels={setShowLabels}
                          />
            </div>
            
            <div className='controlbar' style={iconMenuRibbonStyle}>
              <MenuIconRibbonButtons setSwmmInp={setSwmmInp} swmmTxt={swmmTxt} setOpenRptText={dialogStates['RptText'].setOpen} setEditType={setEditType} setModelRun={setModelRun} loadNew={loadNew} />
            </div>
          </>
        }
    
        <div style={{height: 'calc(100% )'}}>
          <div className='controlbar' style={{color:'white', height: 'calc(100%)'}}>
            <div className='controlrow' style={{color:'white', height: 'calc(100% )'}}>
              {
                !mapOnly &&
                <>
                <div className='controlbar' style={{width:'50px', height: 'calc(100% )', /*backgroundColor: backgroundColor*/}}>
                  <MenuInpTables setupObjectKeysMenu={setupObjectKeysMenu} setKeysSection={setKeysSection} setSubkeysSection={setSubkeysSection} setCreateFunction={setCreateFunction} setDeleteFunction={setDeleteFunction} 
                                openCode={dialogStates['Code'].open} setOpenCode={dialogStates['Code'].setOpen} dialogStates={dialogStates} backgroundColor={backgroundColor} />
                </div>
                </>
              }
              {
                <DialogWindows 
                  dialogParams={dialogParams}
                  dialogPositions={dialogPositions}
                  dialogStates={dialogStates}
                  handleZIndexAssign={handleZIndexAssign} />
              }
              {
                dialogStates['KeysMenu'].open &&
                <ObjectKeysMenu 
                  keysSection={keysSection} 
                  subkeysSection={subkeysSection} 
                  setLayername={setTargetType} 
                  setTargetID={setTargetID} 
                  setOpenOuter={ setOpenMapObject } 
                  setEditType={setEditType} 
                  createFunction={createFunction} 
                  deleteFunction={deleteFunction} 
                  uiDialogPosition={uiDialogPosition} 
                  setUIDialogPosition={setUIDialogPosition} 
                  handleCloseKeysMenu={()=>dialogStates['KeysMenu'].setOpen(false)} 
                  keysDialogPosition={keysDialogPosition} 
                  setKeysDialogPosition={setKeysDialogPosition} 
                  model={model} 
                  setModel={setModel} 
                  handleZIndexAssign={handleZIndexAssign} />
              }
              {
                dialogStates['LIDKeys'].open &&
                <LIDKeysDialog model={model} setModel={setModel} keysSection={'LID_USAGE'} handleClose={()=>dialogStates['LIDKeys'].setOpen(false)} subkeysSection={targetID} uiDialogPosition={uiDialogPosition} setUIDialogPosition={setUIDialogPosition} handleZIndexAssign={handleZIndexAssign} position={dialogPositions['LIDKeys'].position} setPosition={dialogPositions['LIDKeys'].setPosition} />
              }
              
              {
              openMapObject &&
              <DialogWrapper 
                handleClose={handleClose}
                position={uiDialogPosition} 
                setPosition={setUIDialogPosition}
                handleZIndexAssign={handleZIndexAssign} 
                minHeight={650} >
                  <div style={{ height:'calc(100% - 50px)', /*backgroundColor:backgroundColor*/}}>
                    <DemoCodeClickChart {...mapObjectDialogParams.props} />
                  </div>
              </DialogWrapper>
              }
              {
                runSpinner &&
                <ThemeProvider theme={darkTheme} >
                  <Draggable cancel='Button, input, select'  >
                    <div style={{position: 'absolute', right: '20%', width:'300px', zIndex:'20000'}} >
                      <Box className="modal-objectmodal" sx={boxStyle} >
                        <div>Running...</div>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'center'}} >
                          <div className="loader"></div>
                        </div>
                      </Box>
                    </div>
                  </Draggable>
                </ThemeProvider>
              }
              
              <div className='controlbar' style={{width:'100%', backgroundColor: backgroundColor}}>
                {
                  <MapboxGLControl 
                    model={model} setModel={setModel} setOpen={setOpenMapObject} 
                    setTargetType={setTargetType} setTargetID={setTargetID} geoJSONData={geoJSONData} 
                    swmmOut={swmmOut} 
                    targetObjects={{
                      link: Object.keys(model?.CONDUITS?model.CONDUITS:{}), 
                      node: Object.keys(model?.JUNCTIONS?model.JUNCTIONS:{}), 
                      poly: Object.keys(model?.SUBCATCHMENTS?model.SUBCATCHMENTS:{})
                    }}
                    targetParameters={{
                      link:2, 
                      node:3, 
                      poly:4
                    }} 
                    setReport={setRpt} editType={editType} setEditType={setEditType} 
                    modelRun={modelRun} handleClose={handleClose} loadModel={loadModel} 
                    mapStyle={mapStyle} setMapStyle={setMapStyle} view={view} 
                    showLabels={showLabels} />
                }
              </div>
            </div>
          </div>
        </div>

        <div >
          {/* Translate the model into an .inp file when the model is changed */}
          <ModelToText model={model} setSwmmTxt={setSwmmTxt} />

          {/* Run the model, prepare the output and report */}
          <SwmmRun model={model} setSwmmOut={setSwmmOut} setRpt={setRpt} setSwmmTxt={setSwmmTxt} modelRun={modelRun} setRunSpinner={setRunSpinner} setOpenRptText={dialogStates['RptText'].setOpen && showResults} />
        </div>
        
      </ThemeProvider>
    </div>
    </>
  )
}